import { IAdminBulletinItem } from 'src/services/models/bulletins.service';

const DAYS_IN_DEFAULT_RANGE = 90;
const DATE_PART_LENGTH = 2;
const MONTH_ADJUSTMENT = 1;
const DATE_ONLY_LENGTH = 10;
const DATE_START_INDEX = 0;
const FRENCH_LANGUAGE_CODE = 2;
const KEEP_ORDER = 0;
const MOVE_AFTER = 1;
const MOVE_BEFORE = -1;
const START_OF_DAY = 0;

export const BULLETIN_TYPE_CODES: Record<string, number> = {
    Lease: 0,
    Retail: 1,
    Balloon: 2,
    Miscellaneous: 3,
    Informational: 4,
    Programs: 5,
};

/** Types selectable in dropdowns; the other codes only label existing bulletins. */
export const BULLETIN_TYPES = ['Informational', 'Programs'];

/** Bulletin country: 0 when the rule has none, 1 for US, 2 for Canada (any other country). */
export const BULLETIN_COUNTRY_NONE = '0';
export const BULLETIN_COUNTRY_US = '1';
export const BULLETIN_COUNTRY_CANADA = '2';

const US_RULE_COUNTRIES = ['us', 'usa', BULLETIN_COUNTRY_US];

export const BULLETIN_LANGUAGES = ['english', 'french'];

export const BULLETIN_LANGUAGE_CODES: Record<string, number> = {
    english: 1,
    french: 2,
};

export const getBulletinTypeName = (
    bulletinType?: number | string | null,
): string =>
    bulletinType === null || bulletinType === undefined || bulletinType === ''
        ? ''
        : Object.keys(BULLETIN_TYPE_CODES).find(
              (name) => BULLETIN_TYPE_CODES[name] === Number(bulletinType),
          ) ?? '';

export const getBulletinLanguageName = (
    bulletinLanguage?: number | string | null,
): string =>
    Number(bulletinLanguage) === FRENCH_LANGUAGE_CODE ? 'french' : 'english';

/** Bulletin country code for the country of a distribution rule. */
export const getBulletinCountryCode = (
    ruleCountry?: string | number | null,
): string => {
    const normalized = String(ruleCountry ?? '')
        .trim()
        .toLowerCase();

    if (!normalized || normalized === BULLETIN_COUNTRY_NONE) {
        return BULLETIN_COUNTRY_NONE;
    }

    return US_RULE_COUNTRIES.includes(normalized)
        ? BULLETIN_COUNTRY_US
        : BULLETIN_COUNTRY_CANADA;
};

const padDatePart = (value: number): string =>
    `${value}`.padStart(DATE_PART_LENGTH, '0');

/** `Date` to `YYYY-MM-DD`. */
export const formatDate = (date: Date): string =>
    `${date.getFullYear()}-${padDatePart(
        date.getMonth() + MONTH_ADJUSTMENT,
    )}-${padDatePart(date.getDate())}`;

/** `YYYY-MM-DD` to `MM/DD/YYYY`. */
export const formatDateForApi = (value: string): string => {
    const [year, month, day] = value.split('-');

    return `${month}/${day}/${year}`;
};

/** API date (`MM/DD/YYYY` or ISO) to `YYYY-MM-DD`, or '' when invalid. */
export const toDateInputValue = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    if (value.includes('/')) {
        const [month, day, year] = value.split('/');

        return month && day && year
            ? `${year}-${padDatePart(Number(month))}-${padDatePart(
                  Number(day),
              )}`
            : '';
    }

    const dateOnly = value.slice(DATE_START_INDEX, DATE_ONLY_LENGTH);

    return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : '';
};

/** API date (`MM/DD/YYYY` or ISO) to `MM/DD/YYYY` for display. */
export const formatDateOnly = (value?: string | null): string => {
    const [year, month, day] = toDateInputValue(value).split('-');

    return year && month && day ? `${month}/${day}/${year}` : '';
};

export const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date(endDate);

    startDate.setDate(startDate.getDate() - DAYS_IN_DEFAULT_RANGE);

    return {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
    };
};

export const getErrorStatus = (error: unknown): number | undefined => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return undefined;
    }

    return (
        error as {
            response?: {
                status?: number;
            };
        }
    ).response?.status;
};

export const getDateTimestamp = (value?: string | null): number | null => {
    if (!value) {
        return null;
    }

    const parts = value.includes('/')
        ? value.split('/').map(Number)
        : value
              .slice(DATE_START_INDEX, DATE_ONLY_LENGTH)
              .split('-')
              .map(Number);
    const [year, month, day] = value.includes('/')
        ? [parts[2], parts[0], parts[1]]
        : parts;

    if (!year || !month || !day) {
        return null;
    }

    return new Date(year, month - MONTH_ADJUSTMENT, day).getTime();
};

/** Today, or the earliest of the given API dates when they are in the past. */
export const getMinSelectableDate = (
    ...dates: Array<string | null | undefined>
): Date => {
    const earliest = new Date();

    earliest.setHours(START_OF_DAY, START_OF_DAY, START_OF_DAY, START_OF_DAY);

    dates.forEach((value) => {
        const timestamp = getDateTimestamp(value);

        if (timestamp !== null && timestamp < earliest.getTime()) {
            earliest.setTime(timestamp);
        }
    });

    return earliest;
};

/** Expire only applies while the end date is open-ended or after today. */
export const isBulletinExpirable = (validThrough?: string | null): boolean => {
    const endTimestamp = getDateTimestamp(validThrough);

    if (endTimestamp === null) {
        return true;
    }

    const today = new Date();

    today.setHours(START_OF_DAY, START_OF_DAY, START_OF_DAY, START_OF_DAY);

    return endTimestamp > today.getTime();
};

export const sortByEndDate = <T extends { validThrough?: string | null }>(
    items: T[],
    ascending = true,
): T[] =>
    [...items].sort((firstItem, secondItem) => {
        const firstDate = getDateTimestamp(firstItem.validThrough);
        const secondDate = getDateTimestamp(secondItem.validThrough);

        if (firstDate === null && secondDate === null) {
            return KEEP_ORDER;
        }

        if (firstDate === null) {
            return MOVE_AFTER;
        }

        if (secondDate === null) {
            return MOVE_BEFORE;
        }

        return ascending ? firstDate - secondDate : secondDate - firstDate;
    });

export type BulletinSortColumn =
    | 'type'
    | 'rule'
    | 'language'
    | 'startDate'
    | 'endDate'
    | 'modifyDate';
export type BulletinSortDirection = 'asc' | 'desc';

type SortableBulletin = Partial<
    Pick<
        IAdminBulletinItem,
        | 'bulletinType'
        | 'distributionName'
        | 'validFrom'
        | 'validThrough'
        | 'bulletinLanguage'
        | 'modifytime'
    >
>;

const getSortValue = (
    item: SortableBulletin,
    column: BulletinSortColumn,
): string | number | null => {
    switch (column) {
        case 'type':
            return getBulletinTypeName(item.bulletinType) || null;
        case 'rule':
            return item.distributionName || null;
        case 'language':
            return getBulletinLanguageName(item.bulletinLanguage);
        case 'startDate':
            return getDateTimestamp(item.validFrom);
        case 'endDate':
            return getDateTimestamp(item.validThrough);
        default:
            return getDateTimestamp(item.modifytime);
    }
};

/** Sorts by a table column; empty values always go last. */
export const sortBulletins = <T extends SortableBulletin>(
    items: T[],
    column: BulletinSortColumn,
    direction: BulletinSortDirection = 'asc',
): T[] =>
    [...items].sort((firstItem, secondItem) => {
        const firstValue = getSortValue(firstItem, column);
        const secondValue = getSortValue(secondItem, column);

        if (firstValue === null && secondValue === null) {
            return KEEP_ORDER;
        }

        if (firstValue === null) {
            return MOVE_AFTER;
        }

        if (secondValue === null) {
            return MOVE_BEFORE;
        }

        const difference =
            typeof firstValue === 'string'
                ? firstValue.localeCompare(String(secondValue))
                : firstValue - Number(secondValue);

        return direction === 'asc' ? difference : -difference;
    });
